"use client";

import { Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useLocation, useParams } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import NavigationList from "@/components/molecules/NavigationList";
import PageLayout from "@/components/templates/PageLayout";
import { ProfileNavigationIds, profileNavigationItems } from "@/constants/navigation";
import { PWA_ALLOWED_PROFILE_NAV_IDS } from "@/constants/pwa-menu.constants";
// import ROUTE_URLS from "@/constants/route-urls";
import useAuth from "@/contexts/auth-context";
import useGetAllProfiles from "@/hooks/requests/permissions/use-get-all-profiles";
import useGetUserById from "@/hooks/requests/user/use-get-user-by-id";
import { ProfileMessages } from "@/messages/profile/profile.messages";
import { isPWAStandalone } from "@/utils/environment.utils";

import ChangePassword from "./ChangePassword";
import Permissions from "./Permissions";
import UpdateProfile from "./UpdateProfile";
import ProfileSummaryCard from "../../molecules/ProfileSummaryCard";

type Props = {
  messages: ProfileMessages;
  selectedNavigationItem: ProfileNavigationIds;
};

function Profile({ messages, selectedNavigationItem }: Props) {
  const location = useLocation();
  const pathname = location.pathname;
  // const { lang } = useLanguage();
  const { user: authUser } = useAuth();
  const { isPasswordExpired: isPasswordExpiredString } = useParams<{ isPasswordExpired: string }>();
  const { data: user, isLoading: isLoadingUser, error } = useGetUserById();
  const { data: permissionsData, error: errorPermissions } = useGetAllProfiles();

  // const { profile, permissions, changePassword, changePasswordExpired } = ROUTE_URLS;

  const isMainProfilePage =
    pathname.includes("/profile/") &&
    !pathname.includes("/permissions") &&
    !pathname.includes("/change-password");
  const isPermissionsPage = pathname.includes("/profile/permissions/");
  const isChangePasswordExpiredPage = pathname.includes("/profile/change-password-expired/");
  const isChangePasswordPage = pathname.includes("/profile/change-password/") && !isChangePasswordExpiredPage;

  const shouldLoadError = !!error;
  const shouldLoadErrorPermissions = !!errorPermissions && !shouldLoadError && isPermissionsPage;
  const shouldLoadUpdateProfile = !!authUser && user && isMainProfilePage;
  const shouldLoadChangePassword = !!user && (isChangePasswordPage || isChangePasswordExpiredPage);
  const shouldLoadPermissions = !!user && isPermissionsPage && !!permissionsData;
  const shouldLoadProfileSummaryCard =
    (shouldLoadUpdateProfile || shouldLoadChangePassword || shouldLoadPermissions) && user;

  const isPasswordExpired = isPasswordExpiredString === "true";
  const allNavigationItems = profileNavigationItems({ messages });
  const navigationItems = isPWAStandalone()
    ? allNavigationItems.filter((item) => PWA_ALLOWED_PROFILE_NAV_IDS.includes(item._id))
    : allNavigationItems;

  const isLoading = isPasswordExpired
    ? isLoadingUser || (!error && !user)
    : isLoadingUser || (!error && !user && !errorPermissions);

  const shouldLoadUserNotFound = !user && !!error && !errorPermissions && !isLoadingUser;
  return (
    <PageLayout messages={messages} title={messages.pageTitle}>
      <PageContainer>
        <MDBox>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 3 }}>
              <NavigationList items={navigationItems} selectedItem={selectedNavigationItem} />
            </Grid>
            <Grid size={{ xs: 12, lg: 9 }}>
              <MDBox mb={3}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    {isLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={80} />
                    ) : (
                      shouldLoadProfileSummaryCard &&
                      user && <ProfileSummaryCard user={user} userKeyText={messages.userKeyText} />
                    )}
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    {isLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={300} />
                    ) : (
                      <>
                        {shouldLoadUpdateProfile && (
                          <UpdateProfile messages={messages} user={user} />
                        )}

                        {shouldLoadPermissions && (
                          <Permissions
                            messages={messages}
                            user={user}
                            permissions={permissionsData}
                          />
                        )}

                        {shouldLoadChangePassword && (
                          <ChangePassword messages={messages} user={user} />
                        )}
                      </>
                    )}

                    {shouldLoadUserNotFound && (
                      <AlertBlock
                        title={messages.userNotFoundTitle}
                        description={messages.userNotFoundDescription}
                        alt={messages.userNotFoundAlt}
                      />
                    )}
                  </Grid>
                </Grid>
              </MDBox>
            </Grid>
          </Grid>

          {shouldLoadError && (
            <AlertBlock
              title={messages.userErrorToLoadTitle}
              description={error.message}
              alt={messages.userNotFoundAlt}
            />
          )}

          {shouldLoadErrorPermissions && (
            <AlertBlock
              title={messages.permissionErrorToLoadTitle}
              description={errorPermissions.message}
              alt={messages.userNotFoundAlt}
            />
          )}
        </MDBox>
      </PageContainer>
    </PageLayout>
  );
}

export default Profile;
